// ===========================================
// Custom Email Hooks
// ===========================================
// React Query hooks for custom email campaigns feature

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createCustomEmail,
  getCustomEmails,
  getCustomEmail,
  updateCustomEmail,
  deleteCustomEmail,
  duplicateCustomEmail,
  scheduleCustomEmail,
  cancelScheduledEmail,
  getRecipientPreview,
  sendTestEmail,
  sendCustomEmail,
  getCustomEmailStats,
  getDetailedAnalytics,
  getAnalyticsRecords,
  getAttachments,
  uploadAttachment,
  deleteAttachment,
  uploadEmailImage,
} from '../api/customEmail.api';
import {
  CreateCustomEmailData,
  UpdateCustomEmailData,
  ScheduleCustomEmailData,
  TestEmailData,
  CustomEmailFilters,
} from '../types/customEmail.types';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const customEmailKeys = {
  all: ['customEmail'] as const,
  // Lists
  lists: () => [...customEmailKeys.all, 'lists'] as const,
  list: (filters?: CustomEmailFilters) => [...customEmailKeys.lists(), filters] as const,
  // Details
  details: () => [...customEmailKeys.all, 'details'] as const,
  detail: (customEmailId: string) => [...customEmailKeys.details(), customEmailId] as const,
  // Recipients
  recipients: (customEmailId: string) => [...customEmailKeys.all, 'recipients', customEmailId] as const,
  // Analytics
  analytics: () => [...customEmailKeys.all, 'analytics'] as const,
  stats: (customEmailId: string) => [...customEmailKeys.analytics(), 'stats', customEmailId] as const,
  detailed: (customEmailId: string) => [...customEmailKeys.analytics(), 'detailed', customEmailId] as const,
  records: (customEmailId: string, page?: number, limit?: number) =>
    [...customEmailKeys.analytics(), 'records', customEmailId, { page, limit }] as const,
  // Attachments
  attachments: (customEmailId: string) => [...customEmailKeys.all, 'attachments', customEmailId] as const,
};

// ===========================================
// CRUD HOOKS
// ===========================================

/**
 * Hook to fetch all custom emails
 */
/**
 * Poll ONLY while something is in flight, then stop by itself.
 *
 * A send is a background job now: the row goes SENDING the moment the request
 * returns and flips to SENT/FAILED minutes or hours later, with nothing to
 * tell the admin in between. A blanket interval would poll every admin page
 * forever; this one is off unless a row is actually SENDING.
 */
const IN_FLIGHT_POLL_MS = 10_000;

function pollWhileSending<T extends { status?: string }>(rows: T[] | undefined): number | false {
  return rows?.some((r) => r.status === 'SENDING') ? IN_FLIGHT_POLL_MS : false;
}

export function useCustomEmails(filters?: CustomEmailFilters) {
  return useQuery({
    queryKey: customEmailKeys.list(filters),
    queryFn: () => getCustomEmails(filters),
    refetchInterval: (query) => pollWhileSending(query.state.data?.data),
  });
}

/**
 * Hook to fetch a single custom email
 */
export function useCustomEmail(customEmailId: string) {
  return useQuery({
    queryKey: customEmailKeys.detail(customEmailId),
    queryFn: () => getCustomEmail(customEmailId),
    enabled: !!customEmailId && customEmailId !== 'new',
    refetchInterval: (query) => (query.state.data?.status === 'SENDING' ? IN_FLIGHT_POLL_MS : false),
  });
}

/**
 * Hook to create a custom email
 */
export function useCreateCustomEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomEmailData) => createCustomEmail(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
    },
  });
}

/**
 * Hook to update a custom email
 */
export function useUpdateCustomEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customEmailId,
      data,
    }: {
      customEmailId: string;
      data: UpdateCustomEmailData;
    }) => updateCustomEmail(customEmailId, data),
    onSuccess: (_, { customEmailId }) => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customEmailKeys.detail(customEmailId) });
      queryClient.invalidateQueries({ queryKey: customEmailKeys.recipients(customEmailId) });
    },
  });
}

/**
 * Hook to delete a custom email
 */
export function useDeleteCustomEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customEmailId: string) => deleteCustomEmail(customEmailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
    },
  });
}

/**
 * Hook to duplicate a custom email
 */
export function useDuplicateCustomEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customEmailId: string) => duplicateCustomEmail(customEmailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
    },
  });
}

// ===========================================
// SCHEDULING HOOKS
// ===========================================

/**
 * Hook to schedule a custom email
 */
export function useScheduleCustomEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customEmailId,
      data,
    }: {
      customEmailId: string;
      data: ScheduleCustomEmailData;
    }) => scheduleCustomEmail(customEmailId, data),
    onSuccess: (_, { customEmailId }) => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customEmailKeys.detail(customEmailId) });
    },
  });
}

/**
 * Hook to cancel a scheduled custom email
 */
export function useCancelScheduledEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customEmailId: string) => cancelScheduledEmail(customEmailId),
    onSuccess: (_, customEmailId) => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customEmailKeys.detail(customEmailId) });
    },
  });
}

// ===========================================
// RECIPIENT HOOKS
// ===========================================

/**
 * Hook to get recipient preview
 */
export function useRecipientPreview(customEmailId: string) {
  return useQuery({
    queryKey: customEmailKeys.recipients(customEmailId),
    queryFn: () => getRecipientPreview(customEmailId),
    enabled: !!customEmailId && customEmailId !== 'new',
  });
}

// ===========================================
// SENDING HOOKS
// ===========================================

/**
 * Hook to send test email
 */
export function useSendTestEmail() {
  return useMutation({
    mutationFn: ({
      customEmailId,
      data,
    }: {
      customEmailId: string;
      data: TestEmailData;
    }) => sendTestEmail(customEmailId, data),
  });
}

/**
 * Hook to send custom email to all recipients
 */
export function useSendCustomEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customEmailId: string) => sendCustomEmail(customEmailId),
    onSuccess: (_, customEmailId) => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customEmailKeys.detail(customEmailId) });
      queryClient.invalidateQueries({ queryKey: customEmailKeys.stats(customEmailId) });
    },
  });
}

// ===========================================
// ANALYTICS HOOKS
// ===========================================

/**
 * Hook to fetch custom email stats
 */
export function useCustomEmailStats(customEmailId: string) {
  return useQuery({
    queryKey: customEmailKeys.stats(customEmailId),
    queryFn: () => getCustomEmailStats(customEmailId),
    enabled: !!customEmailId,
  });
}

/**
 * Hook to fetch detailed analytics
 */
export function useDetailedAnalytics(customEmailId: string) {
  return useQuery({
    queryKey: customEmailKeys.detailed(customEmailId),
    queryFn: () => getDetailedAnalytics(customEmailId),
    enabled: !!customEmailId,
  });
}

/**
 * Hook to fetch analytics records (delivery log)
 */
export function useAnalyticsRecords(
  customEmailId: string,
  params?: { page?: number; limit?: number }
) {
  return useQuery({
    queryKey: customEmailKeys.records(customEmailId, params?.page, params?.limit),
    queryFn: () => getAnalyticsRecords(customEmailId, params),
    enabled: !!customEmailId,
  });
}

// ===========================================
// ATTACHMENT HOOKS
// ===========================================

/**
 * Hook to fetch attachments for a custom email
 */
export function useAttachments(customEmailId: string) {
  return useQuery({
    queryKey: customEmailKeys.attachments(customEmailId),
    queryFn: () => getAttachments(customEmailId),
    enabled: !!customEmailId && customEmailId !== 'new',
  });
}

/**
 * Hook to upload an attachment
 */
export function useUploadAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customEmailId, file }: { customEmailId: string; file: File }) =>
      uploadAttachment(customEmailId, file),
    onSuccess: (_, { customEmailId }) => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.attachments(customEmailId) });
    },
  });
}

/**
 * Hook to delete an attachment
 */
export function useDeleteAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customEmailId,
      attachmentId,
    }: {
      customEmailId: string;
      attachmentId: string;
    }) => deleteAttachment(customEmailId, attachmentId),
    onSuccess: (_, { customEmailId }) => {
      queryClient.invalidateQueries({ queryKey: customEmailKeys.attachments(customEmailId) });
    },
  });
}

// ===========================================
// IMAGE HOOKS
// ===========================================

/**
 * Hook to upload an image for email content
 * Returns a URL that can be embedded in the email HTML
 */
export function useUploadEmailImage() {
  return useMutation({
    mutationFn: (file: File) => uploadEmailImage(file),
  });
}
